import {
  Calendar,
  CalendarClock,
  Facebook,
  FileText,
  Gamepad2,
  Github,
  HardDrive,
  Linkedin,
  Mail,
  MessageCircleMore,
  MessagesSquare,
  NotebookText,
  Presentation,
  ShoppingBag,
} from "lucide-react";

import { ComposioIntegrationPanel } from "@/components/dashboard/ComposioIntegrationPanel";
import { EmailIntegrationPanel } from "@/components/dashboard/EmailIntegrationPanel";
import { GoogleSheetsIntegrationPanel } from "@/components/dashboard/GoogleSheetsIntegrationPanel";
import { IntegrationCard } from "@/components/dashboard/IntegrationCard";
import { TelegramIntegrationPanel } from "@/components/dashboard/TelegramIntegrationPanel";
import { TelephoneIntegrationPanel } from "@/components/dashboard/TelephoneIntegrationPanel";

import type { Doc } from "@convex/_generated/dataModel";

import type { EmailIntegrationContentProps } from "@/components/dashboard/EmailIntegrationPanel";
import type { useIntegrations } from "@/hooks/useIntegrations";

export interface IntegrationsContentProps {
  email: EmailIntegrationContentProps;
  telegramIntegration: ReturnType<typeof useIntegrations>["telegramIntegration"];
  telegramUsers: ReturnType<typeof useIntegrations>["telegramUsers"];
  onConnectTelegram: ReturnType<typeof useIntegrations>["connectTelegram"];
  onDisconnectTelegram: ReturnType<typeof useIntegrations>["disconnectTelegram"];
  googleSheetsIntegration: ReturnType<typeof useIntegrations>["googleSheetsIntegration"];
  onStartGoogleSheetsOAuth: ReturnType<typeof useIntegrations>["startGoogleSheetsOAuth"];
  onDisconnectGoogleSheets: ReturnType<typeof useIntegrations>["disconnectGoogleSheets"];
  gmailIntegration: ReturnType<typeof useIntegrations>["gmailIntegration"];
  googleDocsIntegration: ReturnType<typeof useIntegrations>["googleDocsIntegration"];
  googleDriveIntegration: ReturnType<typeof useIntegrations>["googleDriveIntegration"];
  googleSlidesIntegration: ReturnType<typeof useIntegrations>["googleSlidesIntegration"];
  googleCalendarIntegration: ReturnType<typeof useIntegrations>["googleCalendarIntegration"];
  notionIntegration: ReturnType<typeof useIntegrations>["notionIntegration"];
  calendlyIntegration: ReturnType<typeof useIntegrations>["calendlyIntegration"];
  linkedinIntegration: ReturnType<typeof useIntegrations>["linkedinIntegration"];
  redditIntegration: ReturnType<typeof useIntegrations>["redditIntegration"];
  shopifyIntegration: ReturnType<typeof useIntegrations>["shopifyIntegration"];
  facebookIntegration: ReturnType<typeof useIntegrations>["facebookIntegration"];
  onStartComposioOAuth: ReturnType<typeof useIntegrations>["startComposioOAuth"];
  onDisconnectComposioIntegration: ReturnType<typeof useIntegrations>["disconnectComposioIntegration"];
  linqNumber: Doc<"linq_numbers"> | null | undefined;
}

const otherIntegrations = [
  { name: "WhatsApp", description: "Control via WhatsApp messages", icon: MessageCircleMore, status: "coming_soon" as const },
  { name: "Discord", description: "Discord bot for DMs and servers", icon: Gamepad2, status: "coming_soon" as const },
  { name: "GitHub", description: "Issues, PRs, repositories", icon: Github, status: "coming_soon" as const },
];

function SectionLabel({ children }: { children: string }) {
  return (
    <h2 className="mb-5 font-body text-[12px] font-bold tracking-[0.16em] text-home-ink/45 uppercase dark:text-white/45">
      {children}
    </h2>
  );
}

// Intrinsic column sizing keyed to the CONTAINER width, not the viewport — so
// the grid reflows correctly inside the narrow floating "Integrations" window
// as well as on the full-width route. min(…,100%) prevents overflow when the
// container is narrower than one card. auto-fit collapses empty trailing tracks
// so a short row (or the 3-card channels row) still fills the width.
const APPS_GRID =
  "grid items-stretch gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]";
const CHANNELS_GRID =
  "grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(260px,100%),1fr))]";

export function IntegrationsContent(props: IntegrationsContentProps) {
  return (
    <div className="min-w-0">
      <div className="mb-12">
        <SectionLabel>Messaging channels</SectionLabel>
        <div className={CHANNELS_GRID}>
          <EmailIntegrationPanel
            agentEmailAddress={props.email.agentEmailAddress}
            allowlist={props.email.allowlist}
            onCreateEmailAddress={props.email.onCreateEmailAddress}
          />
          <TelegramIntegrationPanel
            telegramIntegration={props.telegramIntegration}
            telegramUsers={props.telegramUsers}
            onConnect={props.onConnectTelegram}
            onDisconnect={props.onDisconnectTelegram}
          />
          <TelephoneIntegrationPanel linqNumber={props.linqNumber} />
        </div>
      </div>

      <div className="mb-12">
        <SectionLabel>Apps</SectionLabel>
        <div className={APPS_GRID}>
          <ComposioIntegrationPanel
            displayName="Gmail"
            description="Read, send, and manage emails"
            icon={Mail}
            integration={props.gmailIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "gmail" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "gmail" })}
          />
          <GoogleSheetsIntegrationPanel
            integration={props.googleSheetsIntegration}
            onStartOAuth={props.onStartGoogleSheetsOAuth}
            onDisconnect={props.onDisconnectGoogleSheets}
          />
          <ComposioIntegrationPanel
            displayName="Google Docs"
            description="Read and edit documents"
            icon={FileText}
            integration={props.googleDocsIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "google_docs" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "google_docs" })}
          />
          <ComposioIntegrationPanel
            displayName="Google Slides"
            description="Read and edit presentations"
            icon={Presentation}
            integration={props.googleSlidesIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "google_slides" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "google_slides" })}
          />
          <ComposioIntegrationPanel
            displayName="Google Drive"
            description="Files, folders, sharing, and search"
            icon={HardDrive}
            integration={props.googleDriveIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "google_drive" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "google_drive" })}
          />
          <ComposioIntegrationPanel
            displayName="Google Calendar"
            description="Events, schedules, and availability"
            icon={Calendar}
            integration={props.googleCalendarIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "google_calendar" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "google_calendar" })}
          />
          <ComposioIntegrationPanel
            displayName="Notion"
            description="Pages, databases, and workspace content"
            icon={NotebookText}
            integration={props.notionIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "notion" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "notion" })}
          />
          <ComposioIntegrationPanel
            displayName="Calendly"
            description="Events, scheduling, and availability"
            icon={CalendarClock}
            integration={props.calendlyIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "calendly" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "calendly" })}
          />
          <ComposioIntegrationPanel
            displayName="LinkedIn"
            description="Posts, connections, and professional network"
            icon={Linkedin}
            integration={props.linkedinIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "linkedin" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "linkedin" })}
          />
          <ComposioIntegrationPanel
            displayName="Reddit"
            description="Subreddit posts, search, and comment threads"
            icon={MessagesSquare}
            integration={props.redditIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "reddit" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "reddit" })}
          />
          <ComposioIntegrationPanel
            displayName="Shopify"
            description="Products, orders, customers, and inventory"
            icon={ShoppingBag}
            integration={props.shopifyIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "shopify" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "shopify" })}
          />
          <ComposioIntegrationPanel
            displayName="Facebook"
            description="Pages, posts, and audience insights"
            icon={Facebook}
            integration={props.facebookIntegration}
            onStartOAuth={() => props.onStartComposioOAuth({ provider: "facebook" })}
            onDisconnect={() => props.onDisconnectComposioIntegration({ provider: "facebook" })}
          />
        </div>
      </div>

      <div>
        <SectionLabel>Coming soon</SectionLabel>
        <div className={APPS_GRID}>
          {otherIntegrations.map((integration) => (
            <IntegrationCard key={integration.name} {...integration} />
          ))}
        </div>
      </div>
    </div>
  );
}
